import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button, Chip, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useState } from "react";
import { useCookies } from "react-cookie";

import { downloadHandoff, errorMessage, type HandoverFormat, timeZoneLabel } from "../api";
import type { ArtifactSummary, DraftProgress, DraftSection, SourceRow } from "../types";
import {
  AI_DRAFT,
  DraftProgressChip,
  formatAt,
  Label,
  Meta,
  Summary,
  SummaryHead,
  SummaryText,
  pendingText,
} from "./MissionHeader";
import { HandoffSignOffButton, SignedOffChip } from "./SignOff";

const NOT_RECORDED = "not recorded";
const AI_NOTE =
  "* read from crew messages by AI - check against the messages before relying on it.";

/** The handoff's IMIST-AMBO, ISBAR or ATMIST(ER) letter, in a fixed-width gutter so titles align. */
const Tag = styled("span")(({ theme }) => ({
  display: "inline-block",
  width: theme.spacing(3.5),
  fontWeight: 700,
  color: theme.palette.primary.main,
}));

/** A table block (the care record's vitals) keeps its columns aligned. */
const Table = styled("span")(() => ({
  fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
  fontSize: "0.75rem",
}));

/** A line that has sources: click it to see what they say. */
const LineButton = styled("button")(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  textDecoration: "underline dotted",
  textDecorationColor: theme.palette.text.secondary,
  textUnderlineOffset: "3px",
  "&:hover": { textDecorationStyle: "solid" },
  "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
}));

/** What a line's sources say, opened under it. */
const Sources = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0.5, 0, 0.75),
  padding: theme.spacing(0.75, 1.25),
  borderLeft: `2px solid ${theme.palette.primary.main}`,
  background: theme.palette.background.default,
  fontSize: "0.8125rem",
  whiteSpace: "normal",
  "& > div + div": { marginTop: theme.spacing(0.5) },
}));

const SourceKind = styled("span")(({ theme }) => ({ color: theme.palette.text.secondary }));

/** In this browser's time zone, as the draft's own times are (the server writes them in the zone the page
 *  sends), so a line and what it was taken from show the same clock. */
function at(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function SourceList({ sources, readByAi, zone }: { sources: SourceRow[]; readByAi: boolean; zone: string }) {
  return (
    <Sources>
      {sources.map((source) => (
        <div key={source.event_id}>
          <SourceKind>
            {source.kind} · {at(source.at)} {zone}
            {source.text ? " — " : ""}
          </SourceKind>
          {source.text ? `“${source.text}”` : null}
        </div>
      ))}
      {readByAi ? <SourceKind as="div">Read out of this message by AI.</SourceKind> : null}
    </Sources>
  );
}

/**
 * The draft as the mission summary reads: each section's title with its lines
 * indented under it, the model's part marked "(AI draft)". A line laid out
 * from recorded events can be clicked to show what they say.
 *
 * The handoff (sections carry an IMIST-AMBO or ISBAR letter) keeps every section in
 * order, lettered, including those that say "not recorded": the structure is
 * the point of a handover, and a gap must show where it belongs. Other drafts
 * open with a heading line and gather their "not recorded" sections into one.
 */
function DraftBody({ sections, zone }: { sections: DraftSection[]; zone: string }) {
  const [openLine, setOpenLine] = useState<string | null>(null);
  const lettered = sections.some((s) => s.tag);
  const heading = lettered ? undefined : sections[0];
  const rest = lettered ? sections : sections.slice(1);
  const empty = lettered
    ? []
    : rest.filter((s) => !s.table && s.lines.length === 1 && s.lines[0] === NOT_RECORDED);
  const shown = rest.filter((s) => !empty.includes(s));
  const readByAi = sections.some((s) => s.lines.some((line) => line.includes("*")));

  const renderLine = (section: DraftSection, line: string, key: string, lineIndex: number) => {
    const sources = section.sources?.[lineIndex] ?? [];
    if (section.table) return <Table>{line}</Table>;
    if (sources.length === 0) return line;
    const open = openLine === key;
    return (
      <>
        <LineButton aria-expanded={open} onClick={() => setOpenLine(open ? null : key)}>
          {line}
        </LineButton>
        {open ? <SourceList sources={sources} readByAi={line.includes("*")} zone={zone} /> : null}
      </>
    );
  };

  return (
    <SummaryText as="div">
      {heading ? heading.lines.join(" · ") : null}
      {shown.map((section, index) => (
        <Box key={index} sx={{ mt: heading || index > 0 ? 1.5 : 0 }}>
          {section.tag ? <Tag>{section.tag}</Tag> : null}
          {section.label}
          {section.by_model ? " (AI draft)" : ""}
          {section.lines.map((line, lineIndex) => (
            <Box key={lineIndex} sx={{ pl: section.tag ? 3.5 : 2 }}>
              {renderLine(section, line, `${index}:${lineIndex}`, lineIndex)}
            </Box>
          ))}
        </Box>
      ))}
      {empty.length ? (
        <Box sx={{ mt: 1.5 }}>
          Not recorded
          <Box sx={{ pl: 2 }}>{empty.map((s) => s.label.toLowerCase()).join(", ")}</Box>
        </Box>
      ) : null}
      {readByAi ? <Box sx={{ mt: 1.5 }}>{AI_NOTE}</Box> : null}
      <Box sx={{ mt: 1, color: "text.secondary", fontSize: "0.75rem" }}>
        Times are your local time ({zone}). Click an underlined line to see what it was taken from.
      </Box>
    </SummaryText>
  );
}

type DraftPanelProps = {
  title: string;
  /** The newest version of this draft, if any yet. */
  artifact?: ArtifactSummary;
  emptyText: string;
  /** A newer version on its way, if any. */
  progress?: DraftProgress;
  /** What the draft is, for the "drafting the first ..." text. */
  what: string;
  /** The name of the zone the draft's times are written in (the dashboard's ``time_zone``). */
  zone?: string;
  /** The handoff only: the layout it is shown in, switching it, and why it is the one shown (a tooltip). */
  layout?: { value: HandoverFormat; onChange: (next: HandoverFormat) => void; note?: string };
  /** The handoff only: download it as a PDF, as shown (zone and layout). */
  pdf?: { missionId: string; incident: string };
  /** The handoff only: signing it off, once the mission has ended (`ended`) and the AI is not `drafting` it. */
  signOff?: { missionId: string; ended: boolean; drafting: boolean; onSigned: () => void };
};

/** "PDF": downloads the handoff as the panel shows it; a failure is said beside it. */
function HandoffPdfButton({ missionId, incident }: { missionId: string; incident: string }) {
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await downloadHandoff(token, missionId, incident);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Tooltip title="Download the handoff as a PDF, as shown (layout and time zone)">
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: "1rem !important" }} />}
            disabled={busy || !token}
            onClick={() => void download()}
            // As small as the "Update queued" chip beside it, and as high as the layout dropdown.
            sx={{ height: 26, fontSize: (t) => t.typography.pxToRem(13), "& .MuiButton-startIcon": { mr: 0.5 } }}
          >
            {busy ? "PDF..." : "PDF"}
          </Button>
        </span>
      </Tooltip>
      {error ? <Meta sx={{ color: "error.main" }}>{error}</Meta> : null}
    </>
  );
}

/** The handoff's layouts, as the dropdown offers them: the name, and what it is. */
const LAYOUTS: { value: HandoverFormat; name: string; hint: string }[] = [
  { value: "imist_ambo", name: "IMIST-AMBO", hint: "EMS handover (default)" },
  { value: "isbar", name: "ISBAR", hint: "Norwegian: Trøndelag, Oslo" },
  { value: "atmist", name: "ATMIST(ER)", hint: "UK pre-alert" },
];

/** A small dropdown, as high as the header's "PDF" button beside it. */
function LayoutSelect({
  value,
  onChange,
  note,
}: {
  value: HandoverFormat;
  onChange: (next: HandoverFormat) => void;
  note?: string;
}) {
  return (
    <Tooltip title={note ?? ""} placement="top">
      <TextField
        select
        size="small"
        value={value}
        onChange={(event) => onChange(event.target.value as HandoverFormat)}
        inputProps={{ "aria-label": "Handover layout" }}
        SelectProps={{ renderValue: (v) => LAYOUTS.find((l) => l.value === v)?.name ?? "" }}
        sx={{
          marginLeft: "auto",
          minWidth: 120,
          "& .MuiInputBase-root": { height: 26 },
          "& .MuiSelect-select": { fontSize: "0.75rem", py: 0, pl: 1 },
        }}
      >
        {LAYOUTS.map((layout) => (
          <MenuItem key={layout.value} value={layout.value} sx={{ gap: 1 }}>
            {layout.name}
            <Typography component="span" variant="caption" color="text.secondary">
              {layout.hint}
            </Typography>
          </MenuItem>
        ))}
      </TextField>
    </Tooltip>
  );
}

/** One draft (the handoff), laid out in sections, in the mission summary's
 *  format. */
export function DraftPanel(props: DraftPanelProps) {
  const artifact = props.artifact;
  const signed = artifact?.signed_off;
  // The handoff comes in every layout: show the chosen one at once (the server's own choice otherwise).
  const sections = (props.layout && artifact?.layouts?.[props.layout.value]) || artifact?.sections;

  return (
    <Summary>
      {/* Two rows: the title, "AI draft", the layout and the PDF; then the sign-off (or its button) and version. */}
      <SummaryHead>
        <Label>{props.title}</Label>
        {artifact ? <Chip size="small" color="warning" variant="outlined" label={AI_DRAFT} /> : null}
        {props.layout ? <LayoutSelect {...props.layout} /> : null}
        {props.pdf && artifact ? <HandoffPdfButton {...props.pdf} /> : null}
      </SummaryHead>
      {artifact || props.progress ? (
        <SummaryHead sx={{ mt: -0.5 }}>
          {artifact ? (
            <>
              {signed ? <SignedOffChip signed={signed} /> : null}
              <Meta>
                v{artifact.version} · {formatAt(artifact.created_at)}
              </Meta>
            </>
          ) : null}
          <DraftProgressChip progress={props.progress} hasDraft={Boolean(artifact)} />
          {props.signOff?.ended && artifact && !signed ? (
            <Box sx={{ ml: "auto" }}>
              <HandoffSignOffButton
                missionId={props.signOff.missionId}
                version={artifact.version}
                drafting={props.signOff.drafting}
                onSigned={props.signOff.onSigned}
              />
            </Box>
          ) : null}
        </SummaryHead>
      ) : null}
      {!artifact ? (
        <SummaryText sx={{ color: "text.secondary" }}>
          {pendingText(props.progress, props.what) || props.emptyText}
        </SummaryText>
      ) : (
        <>
          {sections ? (
            <DraftBody sections={sections} zone={props.zone ?? timeZoneLabel()} />
          ) : (
            <SummaryText>{artifact.rendered_text}</SummaryText>
          )}
        </>
      )}
    </Summary>
  );
}
